import classNames from 'classnames';
import { bool, func, string } from 'prop-types';
import React from 'react';

import {
  getItemEventTrackingParams,
  sendTrackingEvent,
  TRACKING_EVENTS,
} from '../../analytics/trackingEvent';
import { useConfiguration } from '../../context/configurationContext';

import { ensureListing, ensureUser } from '../../util/data';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { richText } from '../../util/richText';
import { propTypes } from '../../util/types';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';

import {
  AspectRatioWrapper,
  AvatarSmall,
  IconBookmarkSaved,
  IconBookmarkUnsaved,
  NamedLink,
  ResponsiveImage,
} from '../../components';

import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { formatDateWithProximity, formatDuration } from '../../util/dates';
import { getPriceFromListing } from '../../util/price';
import { toSearchParams } from '../../util/routes';
import {
  isDigitalProductListingType,
  isIndividualSession,
} from '../../util/soulwise-marketplace/listingType';
import { formatDigitalContentSummary } from '../../util/digitalMedia';
import textStyles from './../../styles/text-styles.module.css';
import css from './ListingCard.module.css';
import ListingTags from './ListingTags/ListingTags';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { listing, config, intl } = props;
  // const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);

  const { formattedPrice, priceTitle } = getPriceFromListing(listing, config, intl);
  return (
    <div className={css.price}>
      <div className={`${css.priceValue} ${textStyles.bodyTextB2}`} title={priceTitle}>
        {formattedPrice}
      </div>
      {/*{isBookable ? (*/}
      {/*  <div className={`${css.perUnit} ${textStyles.bodyTextB2}`}>*/}
      {/*    <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />*/}
      {/*  </div>*/}
      {/*) : null}*/}
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    isSaved,
    onSaveClick,
    showSaveButton,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const { location } = publicData || {};
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const authorReviewStats = author?.attributes?.profile?.publicData?.reviewStats;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const duration = listing?.attributes?.publicData?.duration;
  const listingType = listing?.attributes?.publicData?.listingType;
  const isIndividual = isIndividualSession(listingType);
  const isDigitalListing = isDigitalProductListingType(listingType);
  const digitalContentSummary = isDigitalListing
    ? formatDigitalContentSummary(publicData?.digitalMedia?.items, intl)
    : '';
  const { formattedPrice } = getPriceFromListing(listing, config, intl);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  // Tag: #pass-availability-dates-via-url
  const searchParams = toSearchParams(listing?.timeslot?.attributes);
  const eventDate = listing?.timeslot?.attributes?.start;

  const handleListingCardClick = e => {
    sendTrackingEvent({
      name: TRACKING_EVENTS.SELECT_ITEM,
      parameters: {
        items: [getItemEventTrackingParams(listing, config, intl)],
      },
    });
  };

  const handleSaveClick = e => {
    e.preventDefault();
    e.stopPropagation();
    if (onSaveClick) onSaveClick(e);
  };

  const timeslotId = listing?.timeslot?.id?.uuid;

  return (
    <div className={classNames(classes, css.trackingWrapper)} onClick={handleListingCardClick}>
      <div className={css.listingImageWrapper}>
        <NamedLink className={classes} name="ListingPage" params={{ id, slug }} to={searchParams}>
          <AspectRatioWrapper
            className={css.aspectRatioWrapper}
            width={aspectWidth}
            height={aspectHeight}
            {...setActivePropsMaybe}
          >
            <LazyImage
              rootClassName={css.rootForImage}
              alt={title}
              image={firstImage}
              variants={variants}
              sizes={renderSizes}
            />
          </AspectRatioWrapper>
        </NamedLink>
        {showSaveButton && onSaveClick ? (
          <button
            type="button"
            className={css.saveButton}
            onClick={handleSaveClick}
            aria-label={
              isSaved
                ? intl.formatMessage({ id: 'ListingCard.unsaveAriaLabel' })
                : intl.formatMessage({ id: 'ListingCard.saveAriaLabel' })
            }
          >
            {isSaved ? <IconBookmarkSaved /> : <IconBookmarkUnsaved />}
          </button>
        ) : null}
        {showAuthorInfo ? (
          <div className={css.authorInfo}>
            {listing?.author && (
              <AvatarSmall user={listing.author} disableProfileLink className={css.authorAvatar} />
            )}
            <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            {authorReviewStats?.averageRating && (
              <span className={css.rating}>★ {authorReviewStats.averageRating.toFixed(1)}</span>
            )}
          </div>
        ) : null}
      </div>
      <NamedLink className={classes} name="ListingPage" params={{ id, slug }} to={searchParams}>
        <div className={css.info}>
          <ListingTags listing={listing} />
          <div className={css.mainInfo}>
            <div className={textStyles.headlineTextH3}>
              {richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </div>
            {isDigitalListing && digitalContentSummary ? (
              <div className={`${css.date} ${textStyles.bodyTextB3} ${css.lightGreen}`}>
                <FormattedMessage
                  id="ListingCard.digitalContentCounts"
                  values={{ summary: digitalContentSummary }}
                />
              </div>
            ) : null}
            {!isDigitalListing ? (
              <div className={`${css.date} ${textStyles.bodyTextB3} ${css.lightGreen}`}>
                <span className={css.flexible}>
                  {isIndividual
                    ? duration
                      ? formatDuration({ minutes: duration, intl })
                      : intl.formatMessage({ id: 'ListingCard.noDuration' })
                    : listing.timeslot
                    ? formatDateWithProximity(new Date(eventDate), intl)
                    : intl.formatMessage({ id: 'ListingCard.noDate' })}
                </span>
                <span className={css.flexible}>
                  <PlaceOutlinedIcon
                    sx={{ fontSize: '17px', marginInlineStart: '4px', marginInlineEnd: '2px' }}
                  />
                </span>
                <span className={css.truncate} title={location?.venueName}>
                  {location?.venueName ||
                    location?.name ||
                    intl.formatMessage({ id: 'ListingCard.noLocation' })}
                </span>
              </div>
            ) : null}
          </div>
          <PriceMaybe listing={listing} config={config} intl={intl} />
        </div>
      </NamedLink>
    </div>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
  isSaved: false,
  onSaveClick: null,
  showSaveButton: false,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,
  isSaved: bool,
  onSaveClick: func,
  showSaveButton: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
